import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { Button } from "@/shared/foundation/primitives";

import styles from "./DraftRestoreDialog.module.css";

export interface DraftRestoreDialogProps {
  /** Name of the saved dashboard the draft belongs to, or null when the
   *  draft belongs to the unsaved view (no `dashboard` URL param). */
  dashboardName: string | null;
  /** Apply the draft's layout (the edited version). The draft stays in
   *  localStorage — it still represents unsaved edits. */
  onRestoreDraft: () => void;
  /** Keep the baseline already rendered beneath the dialog (saved dashboard
   *  or fresh defaults) and clear the draft so it doesn't resurrect. */
  onDiscardDraft: () => void;
}

/**
 * Asks whether to restore unsaved dashboard edits found in the localStorage
 * draft for the current context. The baseline (the saved dashboard's layout,
 * or the default layout for an unsaved view) is already seeded and renders
 * behind the dialog; "Load the edited version" swaps the draft in, the other
 * choice keeps the baseline and discards the draft.
 *
 * Deliberately not dismissable (no backdrop/Escape close): a dismissal would
 * leave the baseline on screen with the draft still stored, and the next
 * layout edit would then silently overwrite the draft the user never ruled
 * on. Requiring an explicit choice keeps the draft's fate unambiguous.
 */
export function DraftRestoreDialog({
  dashboardName,
  onRestoreDraft,
  onDiscardDraft,
}: DraftRestoreDialogProps) {
  const forSavedDashboard = dashboardName !== null;
  return (
    <Dialog open aria-labelledby="draft-restore-dialog-title">
      <DialogTitle id="draft-restore-dialog-title">
        Restore unsaved edits?
      </DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          {forSavedDashboard ? (
            <>
              You have unsaved edits to <strong>{dashboardName}</strong> from a
              previous visit on this device. Load the edited version, or discard
              it and keep the saved dashboard?
            </>
          ) : (
            <>
              You have an unsaved dashboard view with edits from a previous
              visit on this device. Load the edited version, or start over with
              a new dashboard?
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onDiscardDraft}>
          {forSavedDashboard
            ? "Load the saved dashboard"
            : "Create a new dashboard"}
        </Button>
        <Button variant="contained" onClick={onRestoreDraft}>
          Load the edited version
        </Button>
      </DialogActions>
    </Dialog>
  );
}
